<template lang="pug">
    include ../../icon/arrow

    div(ref="_in",
        class="_ptime _con",
        :class="_class",
        :style="{ width, height }",
        :id="id",
        :inert="inert || disabled",
        :tabindex="inert || disabled ? null : (tabindex || 0)",
        v-on="__on")
        div(class="_ptimei numeric")
            div(class="_ptimec _ptime-hr")
                ib-button(class="link", @click="incHour")
                    +icon-arrow-up4
                div(class="_ptimep")
                    div(v-for="(h,i) in hours", :aria-selected="i == hi", v-text="h")
                    div(v-if="hi < 0", :aria-selected="true", class="fg-error", v-text="ha")
                ib-button(class="link", @click="decHour")
                    +icon-arrow-down4
            div(class="_ptimes") :
            div(class="_ptimec _ptime-min")
                ib-button(class="link", @click="incMinute")
                    +icon-arrow-up4
                div(class="_ptimep")
                    div(v-for="(m,i) in minutes", :aria-selected="i == mi", v-text="m")
                    div(v-if="mi < 0", :aria-selected="true", class="fg-error", v-text="ma")
                ib-button(class="link", @click="decMinute")
                    +icon-arrow-down4
            template(v-if="$date.data.hour12")
                div
                div(class="_ptimec _ptime-pd")
                    ib-button(class="link", :disabled="pi == 1", @click="incPeriod")
                        +icon-arrow-up4
                    div(class="_ptimep")
                        div(v-for="(p,i) in $date.data.period", :aria-selected="i == pi", v-text="p")
                    ib-button(class="link", :disabled="pi == 0", @click="decPeriod")
                        +icon-arrow-down4
</template>

<script>
    import entry from './ib-form-entry.vue';
    import $date from '../../global/date';
    import $locale from '../../global/locale';

    const parser = /^([01]\d|2[0-3]):([0-5]\d)$/;


    /**
     * A time picker rendered similar to a digital clock, with up and down buttons at each digit.
     */
    export default {
        name: 'ib-picker-time',

        mixins: [
            entry
        ],

        props: {
            /**
             * The selected time in HH:MM format; generally use v-model
             */
            value: {
                type: String,
                default: undefined
            },
            /**
             * The minimum time in HH:MM format
             */
            min: {
                type: String,
                default: undefined,
                validator: min => min == null || parser.test(min)
            },
            /**
             * The maximum time in HH:MM format
             */
            max: {
                type: String,
                default: undefined,
                validator: max => max == null || parser.test(max)
            },
            /**
             * Stepping interval in minutes.
             */
            step: {
                type: Number,
                default: 1
            },
            /**
             * Appearance modifier: `frameless` is supported.
             */
            appearance: String
        },

        data() {
            return {
                ha: '',
                ma: '',
                hi: -1,
                mi: -1,
                pi: -1,
                validity: null
            };
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _da: this.disabled,
                    _inv: this.showvalid && !this.valid,
                    ['_e' + this.appearance]: this.appearance
                };
            },

            hours() {
                const h12 = $date.data.hour12;
                const fmt = new Intl.NumberFormat($date.data.locale, { numberingSystem: 'latn', minimumIntegerDigits: h12 ? 1 : 2 });
                const r = new Array(h12 ? 12 : 24);
                for (let i = 0; i < r.length; i++) {
                    r[i] = fmt.format(h12 ? (i || 12) : i);
                }
                return r;
            },

            minutes() {
                const step = this.step;
                if (60 % step || !(step > 0)) {
                    throw new RangeError('step out of range');
                }
                const fmt = new Intl.NumberFormat($date.data.locale, { numberingSystem: 'latn', minimumIntegerDigits: 2 });
                const r = new Array(60 / step);
                for (let i = 0; i < r.length; i++) {
                    r[i] = fmt.format(i * step);
                }
                return r;
            },

            amin() {
                const min = this.min;
                return min ? min.match(parser).slice(1).map(Number) : [];
            },

            amax() {
                const max = this.max;
                return max ? max.match(parser).slice(1).map(Number) : [];
            }
        },

        watch: {
            '$date.data.hour12': '_sync'
        },

        methods: {

            /** @private */
            _sync() {
                let ivalue = this.ivalue;
                let ha, ma;
                if (ivalue == null) {
                    ha = 0;
                    ma = 0;
                } else {
                    [ha,ma] = ivalue.split(':');
                }
                const h12 = $date.data.hour12;
                const hi = +ha;
                const mi = ma / this.step;
                this.ha = ha;
                this.ma = ma;
                this.hi = hi >= 0 && hi <= 23 && hi % 1 == 0 ? h12 && hi > 12 ? hi - 12 : hi : -1;
                this.mi = mi >= 0 && mi <= 59 && mi % 1 == 0 ? mi : -1;
                this.pi = h12 ? +(hi >= 12) : 0;
            },

            decHour() {
                if (!this.hi--) {
                    this.hi = this.hours.length - 1;
                }
                this.change();
            },

            incHour() {
                if (++this.hi >= this.hours.length) {
                    this.hi = 0;
                }
                this.change();
            },

            decMinute() {
                if (--this.mi < 0) {
                    this.mi = this.minutes.length - 1;
                }
                this.change();
            },

            incMinute() {
                if (++this.mi >= this.minutes.length) {
                    this.mi = 0;
                }
                this.change();
            },

            decPeriod() {
                if (this.pi) {
                    this.pi = 0;
                    this.change();
                }
            },

            incPeriod() {
                if (!this.pi) {
                    this.pi = 1;
                    this.change();
                }
            },

            change() {
                const h = this.hi + this.pi * 12;
                const m = this.mi * this.step;
                this._set((h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m));
            },

            validate() {
                const amin = this.amin;
                const amax = this.amax;
                const h = this.hi + this.pi * 12;
                const m = this.mi * this.step;
                let validity;
                if (h < amin[0] || (h == amin[0] && m < amin[1])) {
                    const min = $date.local();
                    min.hour = amin[0];
                    min.minute = amin[1];
                    validity = $locale.data.timeMin.replace('{min}', $date.hm(min));
                }
                if (h > amax[0] || (h == amax[0] && m > amax[1])) {
                    const max = $date.local();
                    max.hour = amax[0];
                    max.minute = amax[1];
                    validity = $locale.data.timeMax.replace('{max}', $date.hm(max));
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return !validity;
            },

            getvalidity() {
                return this.validity;
            }
        }
    };
</script>

<style lang="less">
    @import "../../less/globals.less";

    /** Outer container */
    ._ptime {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        .font-size(@fs3);
        font-weight: 500;
        height: 5em;
        border: 1px solid transparent;

        &._da {
            opacity: .5;
        }
        &._inv {
            border-color: @fg-error;
        }
    }

    /** Inner container */
    ._ptimei {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        max-width: 7em;
    }

    /** Column containing value and up/down buttons */
    ._ptimec {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
        height: 100%;
    }

    /** Separator (vertically centered colon) */
    ._ptimes {
        margin-top: -.125em;
    }

    /** Picker */
    ._ptimep {
        text-align: center;
        > div {
            visibility: hidden;
            height: 0;
        }
        > [aria-selected=true] {
            visibility: visible;
            height: auto;
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div(class="text-center")
            p(class="after-16") Value: 
                ib-field(id="value", v-model="value")
            p(class="before-4")
                ib-picker-time(v-model="value", id="default")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>
